import { config } from "@/lib/config";
import { getSupabase } from "@/lib/supabase";

/**
 * Die Verbindung zur Spaisekammer, von der Oberfläche aus gesehen.
 *
 * Alles läuft über Edge Functions: Das Zugriffstoken der Spaisekammer liegt im
 * Vault und darf den Browser nie sehen (Regel 3 und 9). Die Oberfläche bekommt
 * hier nur Ja, Nein und einen deutschen Satz; die Zeilen kommen wie immer über
 * den Abgleich in Dexie an.
 */

export type PartnerFehler = "code_invalid" | "schon_verbunden" | "nicht_verbunden" | "offline" | "unbekannt";

export interface LinkErgebnis {
  ok: boolean;
  householdName: string | null;
  recipeCount: number;
  /** False, wenn die Spaisekammer unseren Rückkanal nicht angenommen hat. */
  webhook: boolean;
  fehler: PartnerFehler | null;
}

async function zugriffstoken(): Promise<string | null> {
  const client = getSupabase();
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

interface Antwort {
  status: number;
  body: Record<string, unknown> | null;
}

async function rufe(name: string, koerper: Record<string, unknown>): Promise<Antwort> {
  const token = await zugriffstoken();
  if (token === null) return { status: 401, body: null };

  try {
    const antwort = await fetch(`${config().supabaseUrl}/functions/v1/${name}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, apikey: config().supabaseAnonKey, "Content-Type": "application/json" },
      body: JSON.stringify(koerper),
    });
    const text = await antwort.text();
    return { status: antwort.status, body: text.length === 0 ? null : (JSON.parse(text) as Record<string, unknown>) };
  } catch {
    return { status: 0, body: null };
  }
}

function fehlerAus(antwort: Antwort): PartnerFehler {
  if (antwort.status === 0) return "offline";
  const code = antwort.body?.error;
  if (code === "code_invalid") return "code_invalid";
  if (code === "schon_verbunden") return "schon_verbunden";
  if (code === "nicht_verbunden") return "nicht_verbunden";
  return "unbekannt";
}

/** Code einlösen. Die Function verbindet und gleicht einmal ab. */
export async function verbinde(code: string): Promise<LinkErgebnis> {
  const antwort = await rufe("partner-link", { code: code.trim().toUpperCase() });
  if (antwort.status !== 200) {
    return {
      ok: false,
      householdName: typeof antwort.body?.household_name === "string" ? antwort.body.household_name : null,
      recipeCount: 0,
      webhook: false,
      fehler: fehlerAus(antwort),
    };
  }
  return {
    ok: true,
    householdName: typeof antwort.body?.household_name === "string" ? antwort.body.household_name : null,
    recipeCount: typeof antwort.body?.recipe_count === "number" ? antwort.body.recipe_count : 0,
    webhook: antwort.body?.webhook === true,
    fehler: null,
  };
}

/** Abgleich anstoßen. Die Zeilen kommen über Pull und Realtime, nicht hier. */
export async function gleicheAb(): Promise<{ ok: boolean; fehler: PartnerFehler | null }> {
  const antwort = await rufe("partner-sync", {});
  if (antwort.status === 200) return { ok: true, fehler: null };
  return { ok: false, fehler: fehlerAus(antwort) };
}

export async function trenne(): Promise<{ ok: boolean; fehler: PartnerFehler | null }> {
  const antwort = await rufe("partner-unlink", {});
  if (antwort.status === 200) return { ok: true, fehler: null };
  return { ok: false, fehler: fehlerAus(antwort) };
}
